// src/redux/slices/orgSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { fetchOrgs } from "./orgAdminThunk";

const initialFormData = {
  smsFeature: "pushOnly",
  dhundhooLink: "send",
  attendance: "disable",
  logs: "enable",
  management: "disable",
  alerts: "disable",
  planning: "disable",
  tripDetails: "disable",
  paymentWallet: "disable",
  jobRole: "disable",
  vendorConfigurations: "noVendor",
  webApiKey: "",
  vendorHandle: "",
  journeyStatus: "disabled",
  speedLimit: 0,
};

const initialBasicFormData = {
  orgName: "",
  orgAddress: "",
  phoneNumber: "",
  emailId: "",
  alertEmails: [],
  password: "",
  confirmPassword: "",
  avatar: "",
  orgShortCode: "",
  alertEmailIds: [],
  newEmailField: "",
};

const initialExternalInfo = [];

const orgSlice = createSlice({
  name: "org",
  initialState: {
    results: [],
    loading: false,
    error: null,
    formData: initialFormData,
    basicFormData: initialBasicFormData,
    externalInfo: initialExternalInfo,
    selectedOrg: null,           // NEW
    selectedOrgName: "",         // NEW
  },
  reducers: {
    clearResults: (state) => {
      state.results = [];
      state.loading = false;
      state.error = null;
      state.formData = initialFormData;
      state.basicFormData = initialBasicFormData;
      state.externalInfo = initialExternalInfo;
      state.selectedOrg = null;
      state.selectedOrgName = "";
    },

    // Advanced
    updateFormField: (state, action) => {
      const { field, value } = action.payload;
      state.formData[field] = value;
    },
    setFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },

    // Basic
    updateBasicField: (state, action) => {
      const { field, value } = action.payload;
      state.basicFormData[field] = value;
    },
    setBasicFormData: (state, action) => {
      state.basicFormData = { ...state.basicFormData, ...action.payload };
    },
    addAlertEmail: (state, action) => {
      if (!state.basicFormData.alertEmails.includes(action.payload)) {
        state.basicFormData.alertEmails.push(action.payload);
      }
    },
    removeAlertEmail: (state, action) => {
      state.basicFormData.alertEmails = state.basicFormData.alertEmails.filter(
        (_, i) => i !== action.payload
      );
    },

    addAlertEmailId: (state, action) => {
      const email = action.payload.trim();
      if (email && !state.basicFormData.alertEmailIds.includes(email)) {
        state.basicFormData.alertEmailIds.push(email);
        state.basicFormData.newEmailField = state.basicFormData.alertEmailIds.join(", ");
      }
    },
    removeAlertEmailId: (state, action) => {
      state.basicFormData.alertEmailIds = state.basicFormData.alertEmailIds.filter(
        (_, i) => i !== action.payload
      );
      state.basicFormData.newEmailField = state.basicFormData.alertEmailIds.join(", ");
    },

    // External Info
    addExternalInfo: (state) => {
      state.externalInfo.push({ key: "", value: "" });
    },
    updateExternalInfo: (state, action) => {
      const { index, field, value } = action.payload;
      if (state.externalInfo[index]) {
        state.externalInfo[index][field] = value;
      }
    },
    removeExternalInfo: (state, action) => {
      state.externalInfo = state.externalInfo.filter((_, i) => i !== action.payload);
    },

    // NEW REDUCERS
    setSelectedOrg: (state, action) => {
      state.selectedOrg = action.payload;
    },
    setSelectedOrgName: (state, action) => {
      state.selectedOrgName = action.payload;
    },
    clearSelectedOrg: (state) => {
      state.selectedOrg = null;
      state.selectedOrgName = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrgs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrgs.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload;
      })
      .addCase(fetchOrgs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch orgs";
        state.results = [];
      });
  },
});

export const {
  clearResults,
  updateFormField,
  setFormData,
  updateBasicField,
  setBasicFormData,
  addAlertEmail,
  removeAlertEmail,
  addExternalInfo,
  updateExternalInfo,
  removeExternalInfo,
  addAlertEmailId,
  removeAlertEmailId,
  setSelectedOrg,
  setSelectedOrgName,
  clearSelectedOrg,
} = orgSlice.actions;

export default orgSlice.reducer;